import { useEffect, useState } from 'react';
import { getSearchQueryValue } from '@client/shared/utils/urlUtils/urlUtils';
import {
  RESET_PROBLEM,
  confirmReset,
  getResetProblem,
  portalFromContinueUrl,
  verifyResetCode,
} from './passwordReset';
import { getLoginPath } from './portals';

export const RESET_PHASE = {
  CHECKING: 'checking',
  READY: 'ready',
  PROBLEM: 'problem',
};

export const SUBMIT_RESULT = {
  DONE: 'done',
  WEAK_PASSWORD: 'weakPassword',
  LINK_PROBLEM: 'linkProblem',
  FAILED: 'failed',
};

// The state behind a page that sets a password from a Firebase emailed link:
// reads the link's action code and Portal, checks the code before the form is
// shown, and sets the password. Where a Portal's own route already says which
// Portal it is, pass it as `forcedPortal`.
export function usePasswordReset({ search, forcedPortal }) {
  const oobCode = getSearchQueryValue(search, 'oobCode');
  const portal =
    forcedPortal ||
    portalFromContinueUrl(getSearchQueryValue(search, 'continueUrl'));

  const [phase, setPhase] = useState(
    oobCode ? RESET_PHASE.CHECKING : RESET_PHASE.PROBLEM
  );
  const [problem, setProblem] = useState(
    oobCode ? null : RESET_PROBLEM.INVALID_LINK
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!oobCode) {
      return;
    }

    verifyResetCode(oobCode).then(
      () => setPhase(RESET_PHASE.READY),
      (error) => {
        setProblem(getResetProblem(error));
        setPhase(RESET_PHASE.PROBLEM);
      }
    );
  }, [oobCode]);

  const submit = (newPassword) => {
    setIsSaving(true);

    return confirmReset(oobCode, newPassword).then(
      () => SUBMIT_RESULT.DONE,
      (error) => {
        setIsSaving(false);
        const submitProblem = getResetProblem(error);

        if (submitProblem === RESET_PROBLEM.WEAK_PASSWORD) {
          return SUBMIT_RESULT.WEAK_PASSWORD;
        }

        if (submitProblem === RESET_PROBLEM.UNKNOWN) {
          return SUBMIT_RESULT.FAILED;
        }

        setProblem(submitProblem);
        setPhase(RESET_PHASE.PROBLEM);
        return SUBMIT_RESULT.LINK_PROBLEM;
      }
    );
  };

  return {
    portal,
    loginPath: getLoginPath(portal),
    phase,
    problem,
    isSaving,
    submit,
  };
}
